import { HEN_BREEDS, BREED_IDS } from '../../data/breeds';
import { ROOSTER_BREEDS } from '../../data/roosters';
import { ROOSTER_COSTS, VOUCHER_DISCOUNT } from '../../data/balance';
import { buyRooster, releaseRooster, renameFlock, renameRooster } from '../../core/actions';
import { crowStats, roosterCost, roosterSlots } from '../../core/economy';
import { formatDuration, formatMoney } from '../../core/format';
import { ROOSTER_PASSIVES } from '../../core/specials';
import { countHens } from '../../core/state';
import { game } from '../controller';
import { h, progressBar, setProgress, type Bindings } from '../dom';
import { closeModal, confirmModal, openModal } from '../modals';
import { roosterPortrait } from '../portrait';
import { buyButton, card, purchase, refreshShop, section, tag, type Panel } from '../shop';
import { toast } from '../toasts';

function promptName(title: string, current: string, max: number, apply: (name: string) => boolean): void {
  const input = h('input', { type: 'text', value: current, maxlength: max, 'aria-label': title });
  const save = () => { if (apply(input.value)) { closeModal(); game.changed(); refreshShop(true); } };
  input.addEventListener('keydown', (e) => { if (e.key === 'Enter') save(); });
  openModal(h('div', {}, h('h2', { text: title }), input,
    h('div', { class: 'actions' },
      h('button', { class: 'btn secondary', type: 'button', onclick: () => closeModal() }, 'Cancel'),
      h('button', { class: 'btn', type: 'button', onclick: save }, 'Save'))));
}

export const roostersPanel: Panel = {
  id: 'roosters',
  label: 'Roosters',
  badge: () => {
    const sl = roosterSlots(game.state);
    return sl.open > 0 && game.state.money >= roosterCost(game.state) ? 1 : 0;
  },
  key: () => {
    const s = game.state;
    const sl = roosterSlots(s);
    return [sl.open, sl.roosters, s.unlockedBreeds.length, s.vouchers, s.flocks.map((f) => `${f.name}:${f.rooster?.name ?? ''}`).join(',')].join('|');
  },
  build(root: HTMLElement, b: Bindings) {
    const s = game.state;
    const c = game.ctx();
    const status = section('📣 Rooster slots');
    const line = h('div', { class: 'desc' });
    const bar = progressBar(0);
    b.add(() => {
      const sl = roosterSlots(game.state);
      const hens = countHens(game.state);
      if (sl.roosters >= 10) line.textContent = 'All ten roosters hired. Your empire is complete.';
      else if (sl.open > 0) line.textContent = `A rooster slot is open! Rooster #${sl.roosters + 1} costs ${formatMoney(roosterCost(game.state))}.`;
      else line.textContent = `Rooster #${sl.roosters + 1} unlocks at ${sl.nextAtHens} hens (you have ${hens}). It will cost ${formatMoney(roosterCost(game.state))}.`;
      setProgress(bar, sl.open > 0 ? Math.min(1, game.state.money / roosterCost(game.state)) : sl.nextAtHens ? hens / sl.nextAtHens : 1);
    });
    status.append(card({ icon: '🐓', title: [`${roosterSlots(s).roosters}/10 roosters`], desc: line, meta: 'The first rooster joins this flock. Every rooster after that starts a new flock with 15 nests of its own.', extra: bar }));
    if (s.vouchers > 0) status.append(h('p', { class: 'hint', text: `🐣 ${s.vouchers} cockerel voucher${s.vouchers > 1 ? 's' : ''}: your next rooster is ${Math.round(s.vouchers * VOUCHER_DISCOUNT * 100)}% off.` }));
    root.append(status);

    const mine = s.flocks.filter((f) => f.rooster);
    if (mine.length) {
      const sec = section('Your roosters');
      for (const f of mine) {
        const r = f.rooster!;
        const rb = ROOSTER_BREEDS[r.breed];
        const cs = crowStats(s, c, r.breed);
        sec.append(card({
          icon: roosterPortrait(r.breed),
          title: [r.name, tag(rb.name)],
          desc: ROOSTER_PASSIVES[rb.passive].description(rb.aura),
          meta: `${f.name} · crow ×${cs.power.toFixed(1)} laying for ${Math.round(cs.durationMs / 1000)}s every ${formatDuration(cs.cooldownMs)}`,
          side: [
            h('button', { class: 'btn small secondary', type: 'button', onclick: () => promptName('Rename rooster', r.name, 24, (n) => renameRooster(game.state, f.id, n).ok) }, 'Rename'),
            h('button', { class: 'btn small secondary', type: 'button', onclick: () => promptName('Rename flock', f.name, 32, (n) => renameFlock(game.state, f.id, n).ok) }, 'Flock name'),
            h('button', { class: 'btn small danger', type: 'button', onclick: () => confirmModal('Release this rooster?', `${r.name} retires to a hobby farm. No refund, and his flock loses its bonuses until you hire another.`, 'Release', () => { releaseRooster(game.state, f.id); game.changed(); refreshShop(true); }, true) }, 'Release'),
          ],
        }));
      }
      root.append(sec);
    }

    const hire = section('Hire a rooster', 'any breed whose hens you have unlocked');
    const unlocked = BREED_IDS.filter((id) => s.unlockedBreeds.includes(id)).sort((a, x) => ROOSTER_BREEDS[x].aura - ROOSTER_BREEDS[a].aura);
    for (const id of unlocked) {
      const rb = ROOSTER_BREEDS[id];
      const cs = crowStats(s, c, id);
      const buy = buyButton({
        cost: () => roosterCost(game.state),
        enabled: () => roosterSlots(game.state).open > 0,
        onBuy: () => purchase(() => {
          const r = buyRooster(game.state, game.ctx(), id);
          if (r.ok) {
            const newest = game.state.flocks.find((f) => f.rooster?.breed === id && f.rooster.placedWall >= Date.now() - 5000);
            if (newest) game.setView(newest.id);
          }
          return r;
        }),
      }, b);
      hire.append(card({
        icon: roosterPortrait(id),
        title: [rb.name, rb.broodyMod < 1 ? tag('pro-broody') : rb.broodyMod > 1 ? tag('anti-broody') : null].filter(Boolean) as (Node | string)[],
        desc: ROOSTER_PASSIVES[rb.passive].description(rb.aura),
        meta: `Crow: ×${cs.power.toFixed(1)} laying for ${Math.round(cs.durationMs / 1000)}s every ${formatDuration(cs.cooldownMs)} · pair him with ${HEN_BREEDS[id].name} hens${HEN_BREEDS[id].pairBonus > 1 ? ` for +${Math.round((HEN_BREEDS[id].pairBonus - 1) * 100)}% value` : ''}`,
        side: [buy.price, buy.btn],
      }));
    }
    const lockedCount = BREED_IDS.length - unlocked.length;
    if (lockedCount) hire.append(h('p', { class: 'hint', text: `${lockedCount} more rooster breeds unlock along with their hens.` }));
    root.append(hire);
    if (roosterSlots(s).roosters === 0) toastOnce();
  },
};

let hinted = false;
function toastOnce(): void {
  if (hinted) return;
  hinted = true;
  if (game.state.flocks.every((f) => !f.rooster)) toast(`Your first rooster costs ${formatMoney(ROOSTER_COSTS[0])} once you have 5 hens.`, 'info');
}
